import React, { useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { useSearchParams } from "react-router-dom";
import {
	Box,
	Button,
	Typography,
	TextField,
	MenuItem,
	Select,
	FormControl,
	InputLabel,
	Alert,
	CircularProgress,
	SelectChangeEvent,
	Paper,
	Grid,
	Card,
	Chip,
	Collapse,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { motion } from "framer-motion";
import SignupService from "../../servicos/signup/SignupService";
import { Business, Person, CheckCircle, Close } from "@mui/icons-material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import StripeService from "../../services/stripe/StripeService";
import {
	TierId,
	TIER_DISPLAY,
	coerceTierId,
	formatTierPrice,
} from "../../config/tierDisplay";
import { usePlanCatalog } from "../../hooks/usePlanCatalog";
import SignupStepper from "../../components/SignupStepper";

// PDF layout: left half is clean white (form area), right half is the
// vertical mint → yellow-green → cream gradient (branded panel).
// Full-bleed — no outer white border. The gradient panel reaches the
// viewport's right edge and the form panel reaches the left edge.
//
// Implementation: horizontal split background (white left / gradient right)
// at md+. On smaller screens the Grid stacks so we fall back to plain white;
// the PricingSection has its own gradient and will paint correctly.
const PageContainer = styled(Box)(({ theme }) => ({
	position: "relative",
	minHeight: "100vh",
	width: "100%",
	background: "#FFFFFF",
	[theme.breakpoints.up("md")]: {
		background:
			"linear-gradient(to right, #FFFFFF 0%, #FFFFFF 50%, #DBF0EC 50%, #F6F3DF 100%)",
	},
	overflow: "hidden",
	padding: 0,
	margin: 0,
}));

const SignupCard = styled(Paper)(({ theme }) => ({
	position: "relative",
	width: "100%",
	minHeight: "100vh",
	background: "transparent",
	borderRadius: 0,
	boxShadow: "none",
	border: "none",
	zIndex: 10,
	overflow: "auto",
	[theme.breakpoints.down("sm")]: {
		borderRadius: 0,
	},
}));

const FormSection = styled(Box)(({ theme }) => ({
	padding: "48px 56px",
	background: "#ffffff",
	minHeight: "100%",
	[theme.breakpoints.down("md")]: {
		padding: "32px",
	},
	[theme.breakpoints.down("sm")]: {
		padding: "24px",
	},
}));

const SectionTitle = styled(Typography)(() => ({
	fontSize: "12px",
	fontWeight: 700,
	letterSpacing: "1px",
	textTransform: "uppercase",
	color: "#2BA6AC",
	marginBottom: "16px",
	display: "flex",
	alignItems: "center",
	gap: "8px",
}));

const StyledTextField = styled(TextField)(() => ({
	"& .MuiOutlinedInput-root": {
		borderRadius: "14px",
		backgroundColor: "#EDEEF5",
		transition: "all 0.2s ease",
		"& fieldset": {
			borderColor: "transparent",
		},
		"&:hover": {
			backgroundColor: "#E5E7F0",
			"& fieldset": {
				borderColor: "transparent",
			},
		},
		"&.Mui-focused": {
			backgroundColor: "#FFFFFF",
			"& fieldset": {
				borderColor: "#2BA6AC",
				borderWidth: "2px",
			},
		},
		"& input": {
			padding: "16px 18px",
			fontSize: "15px",
			color: "#1A2B4A",
		},
	},
	"& .MuiInputLabel-root": {
		fontSize: "14px",
		color: "#1A2B4A",
		fontWeight: 500,
		"&.Mui-focused": {
			color: "#2BA6AC",
		},
	},
	"& .MuiFormLabel-asterisk": {
		color: "#E91E63",
	},
}));

const StyledFormControl = styled(FormControl)(() => ({
	"& .MuiOutlinedInput-root": {
		borderRadius: "14px",
		backgroundColor: "#EDEEF5",
		transition: "all 0.2s ease",
		"& fieldset": {
			borderColor: "transparent",
		},
		"&:hover": {
			backgroundColor: "#E5E7F0",
			"& fieldset": {
				borderColor: "transparent",
			},
		},
		"&.Mui-focused": {
			backgroundColor: "#FFFFFF",
			"& fieldset": {
				borderColor: "#2BA6AC",
				borderWidth: "2px",
			},
		},
	},
	"& .MuiInputLabel-root": {
		fontSize: "14px",
		color: "#1A2B4A",
		fontWeight: 500,
		"&.Mui-focused": {
			color: "#2BA6AC",
		},
	},
	"& .MuiFormLabel-asterisk": {
		color: "#E91E63",
	},
}));

const SubmitButton = styled(Button)(() => ({
	background: "#2BA6AC",
	color: "#ffffff",
	fontWeight: 500,
	fontSize: "18px",
	textTransform: "lowercase",
	letterSpacing: "0.5px",
	borderRadius: "8px",
	padding: "16px 32px",
	marginTop: "32px",
	width: "100%",
	boxShadow: "none",
	transition: "background 0.2s ease",
	"&:hover": {
		background: "#1F8A8F",
		boxShadow: "none",
	},
	"&:disabled": {
		background: "#dee2e6",
		color: "#6c757d",
	},
}));

const PricingSection = styled(Box)(({ theme }) => ({
	padding: "48px 56px",
	// PDF right panel — vertical mint → yellow-green → cream gradient
	// (matches the Auth0 hosted login backdrop for visual continuity).
	background:
		"linear-gradient(180deg, #DBF0EC 0%, #E8F1D6 55%, #F6F3DF 100%)",
	height: "100%",
	display: "flex",
	flexDirection: "column",
	[theme.breakpoints.down("md")]: {
		padding: "32px 24px",
	},
}));

const PricingCard = styled(Card, {
	shouldForwardProp: (prop) => prop !== "isSelected",
})<{ isSelected: boolean }>(() => ({
	padding: "28px",
	borderRadius: "16px",
	border: "1px solid rgba(43,166,172,0.18)",
	boxShadow: "0 6px 22px rgba(26, 43, 74, 0.06)",
	background: "#FFFFFF",
	transition: "box-shadow 0.2s ease",
}));

const FeatureItem = styled(Box, {
	shouldForwardProp: (prop) => prop !== "included",
})<{ included: boolean }>(({ included }) => ({
	display: "flex",
	alignItems: "center",
	marginBottom: "12px",
	color: included ? "#2c3e50" : "#6c757d",
	opacity: included ? 1 : 0.6,
}));

const Signup: React.FC = () => {
	const {
		isAuthenticated,
		isLoading,
		user,
		getAccessTokenSilently,
		loginWithRedirect,
	} = useAuth0();
	const [searchParams, setSearchParams] = useSearchParams();
	const planFromUrl = coerceTierId(searchParams.get("plan"));
	// Segmentation tiers bypass the legacy TierId coercion: keep the raw
	// plan for the signup payload (P4 supplier-request flywheel).
	const rawPlanParam = searchParams.get("plan");
	const segmentationPlan: "measure" | "enable" | null =
		rawPlanParam === "measure" || rawPlanParam === "enable"
			? rawPlanParam
			: null;
	const supplierRequestToken = searchParams.get("supplierRequest");

	const [formData, setFormData] = useState({
		// Company Information
		companyName: "",
		griSector: "",
		organizationType: "",

		// Primary Contact Information
		firstName: "",
		lastName: "",
		email: "",
		jobTitle: "",

		// Legacy fields (to maintain compatibility)
		fullName: "",
		company: "",
		role: "",
		referralSource: "",
		projectDescription: "",
	});

	const [loading, setLoading] = useState(false);
	const [error, setError] = useState("");
	const [success, setSuccess] = useState(false);

	// Pricing state — tier comes from ?plan= or defaults to Starter.
	const [selectedPlan, setSelectedPlan] = useState<TierId>(planFromUrl);
	const [showExclusions, setShowExclusions] = useState(false);
	const stripeService = StripeService.getInstance();
	const { planFor, loading: catalogLoading } = usePlanCatalog();
	const handleChangePlan = (tier: TierId) => {
		setSelectedPlan(tier);
		setShowExclusions(false);
		// Reflect the choice in the URL so a refresh keeps the same plan and
		// any analytics see the correct landing.
		const next = new URLSearchParams(searchParams);
		next.set("plan", tier);
		setSearchParams(next, { replace: true });
	};

	const griSectorOptions = [
		"Energy",
		"Materials",
		"Industrials",
		"Consumer Discretionary",
		"Consumer Staples",
		"Health Care",
		"Financials",
		"Information Technology",
		"Communication Services",
		"Utilities",
		"Real Estate",
		"Other",
	];

	const organizationTypeOptions = [
		"Corporation",
		"Limited Liability Company (LLC)",
		"Partnership",
		"Sole Proprietorship",
		"Non-Profit Organization",
		"Government Agency",
		"Other",
	];

	// Auto-redirect unauthenticated visitors straight to the Auth0 signup
	// screen, round-tripping the chosen plan via appState. Marketing-site
	// deep links (e.g. /signup?plan=core) feel like a single click.
	React.useEffect(() => {
		if (isLoading) return;
		if (!isAuthenticated) {
			// Keep every signup parameter (plan, supplierRequest, …) across the
			// Auth0 round trip: dropping the supplierRequest token here would
			// silently lose supplier-signup attribution for new users.
			loginWithRedirect({
				appState: {
					returnTo: `/signup${window.location.search || `?plan=${planFromUrl}`}`,
				},
				authorizationParams: { screen_hint: "signup" },
			});
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isAuthenticated, isLoading]);

	React.useEffect(() => {
		if (user?.email) {
			const nameParts = user.name?.split(" ") || [];
			const firstName = nameParts[0] || "";
			const lastName = nameParts.slice(1).join(" ") || "";

			setFormData((prev) => ({
				...prev,
				email: user.email || "",
				fullName: user.name || "",
				firstName: firstName,
				lastName: lastName,
				// Also populate legacy fields immediately
				company: prev.companyName || "",
				role: prev.jobTitle || "",
			}));
		}
	}, [user]);

	const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const { name, value } = e.target;
		setFormData((prev) => {
			const newData = {
				...prev,
				[name]: value,
			};

			// Sync legacy fields automatically
			if (name === "companyName") {
				newData.company = value;
			} else if (name === "jobTitle") {
				newData.role = value;
			} else if (name === "firstName" || name === "lastName") {
				newData.fullName =
					`${name === "firstName" ? value : newData.firstName} ${name === "lastName" ? value : newData.lastName}`.trim();
			}

			return newData;
		});
	};

	const handleSelectChange = (e: SelectChangeEvent) => {
		const { name, value } = e.target;
		setFormData((prev) => ({
			...prev,
			[name || "referralSource"]: value,
		}));
	};

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();

		setError("");
		setLoading(true);

		try {
			const accessToken = await getAccessTokenSilently({
				authorizationParams: {
					audience: "https://api.arteh.com",
					scope: "openid profile email",
				},
			});

			// Prepare data with only the fields we actually collect
			const signupData = {
				// Company Information (only collected fields)
				companyName: formData.companyName,
				griSector: formData.griSector,
				organizationType: formData.organizationType,
				// Primary Contact Information (only collected fields)
				firstName: formData.firstName,
				lastName: formData.lastName,
				email: formData.email,
				jobTitle: formData.jobTitle,
				// Selected plan — backend persists this into companies.current_plan
				plan: segmentationPlan ?? selectedPlan,
				supplierRequest: supplierRequestToken || undefined,
				// Legacy fields for backward compatibility
				company: formData.companyName || "Unknown Company",
				fullName:
					formData.fullName ||
					`${formData.firstName} ${formData.lastName}`.trim() ||
					"Unknown User",
				role: formData.jobTitle || "Unknown Role",
				referralSource: "Direct", // Default value for tracking
				projectDescription: "", // Not collected anymore
				auth0Sub: user?.sub || "",
				// NOT sending: companyState, companyCountry, contactState, contactCountry, phone, mobilePhone
			};

			const response = await SignupService.signup(
				signupData,
				accessToken,
			);

			// Extract response data
			const {
				companyId,
				companyName,
				artehToken,
				demoCompanyId,
				demoToken,
			} = response;

			// Step 2: Update Auth0 metadata and create Stripe customer in parallel

			const auth0Payload = {
				userId: user?.sub,
				user_metadata: {
					activeCompanyId: demoCompanyId,
					isDemoMode: true,
				},
				app_metadata: {
					companies: [
						{
							id: String(companyId),
							name: companyName,
							token: artehToken,
							type: "real",
						},
						{
							id: demoCompanyId,
							name: "Demo Company",
							token: demoToken,
							type: "demo",
						},
					],
				},
			};

			const [_auth0Response, stripeResponse] = await Promise.all([
				// Update Auth0 metadata (critical - must succeed)
				fetch(
					`${process.env.REACT_APP_LAMBDA_ARTEH_API_DOMAIN}/api/auth0/signup-metadata`,
					{
						method: "POST",
						headers: {
							Authorization: `Bearer ${accessToken}`,
							"Content-Type": "application/json",
						},
						body: JSON.stringify(auth0Payload),
					},
				).then((response) => {
					if (!response.ok) {
						throw new Error("Failed to update Auth0 metadata");
					}

					return response;
				}),

				// Create Stripe customer (non-critical - can fail silently)
				// Using the new endpoint that doesn't require auth
				(async () => {
					try {
						const stripeRequestBody = {
							companyId: String(companyId),
							email: formData.email,
							companyName: companyName,
							contactName:
								formData.fullName ||
								`${formData.firstName} ${formData.lastName}`.trim(),
						};

						// Use the new signup-specific endpoint that doesn't require auth
						const response = await fetch(
							`${process.env.REACT_APP_LAMBDA_ARTEH_API_DOMAIN}/api/signup/create-stripe-customer`,
							{
								method: "POST",
								headers: {
									"Content-Type": "application/json",
									// No Authorization header needed for this endpoint
								},
								body: JSON.stringify(stripeRequestBody),
							},
						);

						if (response.ok) {
							const stripeData = await response.json();

							// Return the data for later use; the customerId is
							// extracted and validated from it further down.
							return stripeData;
						} else {
							try {
								const _errorText = await response.text();
							} catch (e) {}
						}
						return null;
					} catch (fetchError: any) {
						console.error(
							"💳 ❌ Stripe customer creation failed (non-critical):",
							fetchError,
						);
						console.error("💳 Error details:", {
							name: fetchError?.name || "Unknown",
							message: fetchError?.message || "Unknown error",
							stack: fetchError?.stack || "No stack trace",
						});
						// Continue anyway - Stripe customer can be created later
						return null;
					}
				})(),
			]);

			setSuccess(true);

			// Every paid tier → Stripe Checkout; only Starter skips payment.
			// (This previously matched the legacy core/premium names only, so
			// measure/enable signups skipped checkout and got no subscription.)
			if (selectedPlan !== "starter") {
				// Wait 2 seconds to ensure database update completes
				await new Promise((resolve) => setTimeout(resolve, 2000));

				try {
					const customerId =
						stripeResponse?.Data?.customerId ||
						stripeResponse?.customerId;

					if (!customerId) {
						const errorMsg =
							"Stripe customer creation failed. Unable to process payment. Please contact support or try upgrading from your dashboard after signup.";
						console.error("❌ " + errorMsg);
						setError(errorMsg);
						setLoading(false);
						setTimeout(() => {
							window.location.href = "/dashboard";
						}, 5000);
						return;
					}

					// Stripe price ID comes from /api/plan-catalog (single source of truth)
					const plan = planFor(selectedPlan);
					const priceId = plan?.stripePriceId;
					if (!priceId) {
						const errorMsg = `The ${selectedPlan} plan is not yet configured for checkout. Please contact support or try again later.`;
						console.error("❌ " + errorMsg);
						setError(errorMsg);
						setLoading(false);
						setTimeout(() => {
							window.location.href = "/dashboard";
						}, 5000);
						return;
					}

					const { sessionId } =
						await stripeService.createCheckoutSession(
							priceId,
							customerId,
							accessToken,
							{
								successUrl: `${window.location.origin}/signup/success?plan=${selectedPlan}&session_id={CHECKOUT_SESSION_ID}`,
								cancelUrl: `${window.location.origin}/signup?plan=${selectedPlan}&payment=cancelled`,
							},
						);

					const stripe = await stripeService.getStripe();
					if (!stripe) {
						throw new Error("Failed to load Stripe");
					}

					await stripe.redirectToCheckout({ sessionId });
				} catch (stripeError: any) {
					console.error(
						"❌ Failed to create Stripe checkout session:",
						stripeError,
					);
					const errorMsg = `Payment setup failed: ${stripeError.message || stripeError.toString() || "Unknown error"}. You can upgrade from your dashboard after signup.`;
					setError(errorMsg);
					setLoading(false);
					setTimeout(() => {
						window.location.href = "/dashboard";
					}, 5000);
				}
			} else {
				// Starter — no payment step, go straight to confirmation
				setLoading(false);
				window.location.href = `/signup/success?plan=${selectedPlan}`;
			}
		} catch (err: any) {
			console.error("Signup error:", err);
			setError(err.message || "Registration failed. Please try again.");
			setLoading(false);
		}
	};

	if (isLoading) {
		return (
			<PageContainer>
				<CircularProgress sx={{ color: "#5DBEC3" }} />
			</PageContainer>
		);
	}

	// While the auto-redirect effect dispatches to Auth0, show a spinner.
	// No interstitial button — marketing deep links should feel like a
	// single click straight into the Auth0 signup form.
	if (!isAuthenticated) {
		return (
			<PageContainer>
				<CircularProgress sx={{ color: "#5DBEC3" }} />
			</PageContainer>
		);
	}

	return (
		<PageContainer>
			<motion.div
				initial={{ opacity: 0 }}
				animate={{ opacity: 1 }}
				transition={{ duration: 0.4, ease: "easeOut" }}
				style={{ width: "100%" }}
			>
				<SignupCard>
					{/* Stepper sits above both columns. Split background so the
					    left half (above the form) stays white and the right half
					    (above the pricing panel) shows the mint of the gradient
					    top — no full-width white bleed past the midline.        */}
					<Box
						sx={{
							width: "100%",
							background: {
								xs: "#FFFFFF",
								md: "linear-gradient(to right, #FFFFFF 0%, #FFFFFF 50%, #DBF0EC 50%, #DBF0EC 100%)",
							},
						}}
					>
						<SignupStepper currentStep="business_details" />
					</Box>

					<Grid container sx={{ minHeight: "100vh" }}>
						{/* Left side - Signup Form (50%) */}
						<Grid item xs={12} md={6}>
							<FormSection>
								<Typography
									sx={{
										color: "#2BA6AC",
										fontWeight: 700,
										fontSize: "40px",
										lineHeight: 1.1,
										marginBottom: "8px",
										letterSpacing: "-0.5px",
									}}
								>
									Register Your Business
								</Typography>

								<Typography
									sx={{
										color: "#1A2B4A",
										fontWeight: 400,
										fontSize: "16px",
										marginBottom: "40px",
									}}
								>
									A few details and you're on your way
								</Typography>

								<Alert severity="info" sx={{ mb: 3 }}>
									Migrating from the old ARTEH platform?{" "}
									<a
										href="/claim"
										style={{
											color: "#2BA6AC",
											fontWeight: 600,
										}}
									>
										Claim your existing company
									</a>{" "}
									instead.
								</Alert>

								{error && (
									<Alert severity="error" sx={{ mb: 3 }}>
										{error}
									</Alert>
								)}

								{success && (
									<Alert severity="success" sx={{ mb: 3 }}>
										Registration successful! Redirecting to
										dashboard...
									</Alert>
								)}

								<Box component="form" onSubmit={handleSubmit}>
									{/* Company Information Section */}
									<SectionTitle>
										<Business sx={{ fontSize: "18px" }} />
										Company Information
									</SectionTitle>

									<Box
										sx={{
											display: "grid",
											gridTemplateColumns: "1fr",
											gap: "24px",
											marginBottom: "32px",
										}}
									>
										<Box>
											<StyledTextField
												required
												fullWidth
												label="Company Name"
												name="companyName"
												autoComplete="off"
												inputProps={{
													autoComplete: "off",
												}}
												value={formData.companyName}
												onChange={handleInputChange}
												placeholder="Company Name"
											/>
										</Box>

										<Box>
											<StyledFormControl
												fullWidth
												required
											>
												<InputLabel>
													GRI Sector Classification
												</InputLabel>
												<Select
													name="griSector"
													value={formData.griSector}
													onChange={
														handleSelectChange
													}
													label="GRI Sector Classification"
													sx={{
														"& .MuiSelect-select": {
															padding:
																"14px 16px",
														},
													}}
												>
													{griSectorOptions.map(
														(option) => (
															<MenuItem
																key={option}
																value={option}
															>
																{option}
															</MenuItem>
														),
													)}
												</Select>
											</StyledFormControl>
										</Box>

										<Box>
											<StyledFormControl
												fullWidth
												required
											>
												<InputLabel>
													Type of Organization
												</InputLabel>
												<Select
													name="organizationType"
													value={
														formData.organizationType
													}
													onChange={
														handleSelectChange
													}
													label="Type of Organization"
													sx={{
														"& .MuiSelect-select": {
															padding:
																"14px 16px",
														},
													}}
												>
													{organizationTypeOptions.map(
														(option) => (
															<MenuItem
																key={option}
																value={option}
															>
																{option}
															</MenuItem>
														),
													)}
												</Select>
											</StyledFormControl>
										</Box>
									</Box>

									{/* Primary Contact Information Section */}
									<SectionTitle>
										<Person sx={{ fontSize: "18px" }} />
										Primary Contact Information
									</SectionTitle>

									<Box
										sx={{
											display: "grid",
											gridTemplateColumns: "1fr",
											gap: "24px",
											marginBottom: "24px",
										}}
									>
										<Box>
											<StyledTextField
												required
												fullWidth
												label="First Name"
												name="firstName"
												autoComplete="given-name"
												inputProps={{
													"data-form-type": "other",
													autoComplete: "given-name",
												}}
												value={formData.firstName}
												onChange={handleInputChange}
												placeholder="First Name"
											/>
										</Box>

										<Box>
											<StyledTextField
												required
												fullWidth
												label="Last Name"
												name="lastName"
												autoComplete="family-name"
												inputProps={{
													"data-form-type": "other",
													autoComplete: "family-name",
												}}
												value={formData.lastName}
												onChange={handleInputChange}
												placeholder="Last Name"
											/>
										</Box>

										<Box>
											<StyledTextField
												required
												fullWidth
												label="Email"
												name="email"
												type="email"
												autoComplete="email"
												disabled
												inputProps={{
													"data-form-type": "other",
													autoComplete: "email",
												}}
												value={formData.email}
												placeholder="Email"
												helperText="Email from your Auth0 account"
											/>
										</Box>

										<Box>
											<StyledTextField
												required
												fullWidth
												label="Job Title"
												name="jobTitle"
												autoComplete="off"
												inputProps={{
													"data-form-type": "other",
													autoComplete: "off",
												}}
												value={formData.jobTitle}
												onChange={handleInputChange}
												placeholder="Job Title"
											/>
										</Box>
									</Box>

									<SubmitButton
										type="submit"
										fullWidth
										variant="contained"
										disabled={loading}
									>
										{loading ? (
											<CircularProgress
												size={24}
												sx={{ color: "#fff" }}
											/>
										) : selectedPlan === "starter" ? (
											"Create my account"
										) : catalogLoading ? (
											"Continue to payment"
										) : (
											`Continue to payment — ${formatTierPrice(planFor(selectedPlan))} / year`
										)}
									</SubmitButton>
								</Box>
							</FormSection>
						</Grid>

						{/* Right side - Order summary (50%) */}
						<Grid item xs={12} md={6}>
							<PricingSection>
								<Box sx={{ mb: 3 }}>
									<Typography
										sx={{
											color: "#1A2B4A",
											fontWeight: 700,
											marginBottom: "6px",
											fontSize: "22px",
										}}
									>
										Choose your plan
									</Typography>
									<Typography
										variant="body2"
										sx={{ color: "#5A6E7F" }}
									>
										Pick a tier — the summary on the right
										updates instantly.
									</Typography>
								</Box>

								{/* Plan selector — segmented control */}
								<Box
									sx={{
										display: "grid",
										gridTemplateColumns:
											"repeat(3, minmax(0, 1fr))",
										gap: "12px",
										marginBottom: "24px",
									}}
								>
									{(
										[
											"starter",
											"core",
											"premium",
										] as TierId[]
									).map((tier) => {
										const isActive = selectedPlan === tier;
										const display = TIER_DISPLAY[tier];
										const priceLabel =
											tier === "starter"
												? "Free"
												: catalogLoading
													? "…"
													: formatTierPrice(
															planFor(tier),
														);
										return (
											<Box
												key={tier}
												role="button"
												tabIndex={0}
												onClick={() =>
													handleChangePlan(tier)
												}
												onKeyDown={(e) => {
													if (
														e.key === "Enter" ||
														e.key === " "
													) {
														e.preventDefault();
														handleChangePlan(tier);
													}
												}}
												sx={{
													position: "relative",
													padding: "16px 14px",
													borderRadius: "12px",
													cursor: "pointer",
													background: isActive
														? "#FFFFFF"
														: "rgba(255,255,255,0.55)",
													border: isActive
														? "2px solid #2BA6AC"
														: "2px solid rgba(43,166,172,0.15)",
													boxShadow: isActive
														? "0 6px 18px rgba(43,166,172,0.18)"
														: "none",
													transition: "all 0.2s ease",
													textAlign: "left",
													outline: "none",
													"&:hover": {
														borderColor: isActive
															? "#2BA6AC"
															: "rgba(43,166,172,0.4)",
														background: "#FFFFFF",
													},
													"&:focus-visible": {
														outline:
															"2px solid #2BA6AC",
														outlineOffset: "2px",
													},
												}}
											>
												{display.featured && (
													<Chip
														label="Popular"
														size="small"
														sx={{
															position:
																"absolute",
															top: "-10px",
															right: "10px",
															height: "20px",
															fontSize: "10px",
															fontWeight: 700,
															letterSpacing:
																"0.5px",
															backgroundColor:
																"#2BA6AC",
															color: "#fff",
														}}
													/>
												)}
												<Typography
													sx={{
														color: isActive
															? "#2BA6AC"
															: "#1A2B4A",
														fontWeight: 700,
														fontSize: "16px",
														marginBottom: "4px",
													}}
												>
													{display.name}
												</Typography>
												<Typography
													sx={{
														color: "#5A6E7F",
														fontSize: "13px",
														fontWeight: 500,
													}}
												>
													{priceLabel}
													{tier !== "starter" && (
														<Typography
															component="span"
															sx={{
																fontSize:
																	"11px",
																color: "#8896A4",
																ml: 0.5,
															}}
														>
															/ year
														</Typography>
													)}
												</Typography>
											</Box>
										);
									})}
								</Box>

								{/* Selected plan summary card */}
								<PricingCard isSelected={true}>
									<Box
										sx={{
											display: "flex",
											justifyContent: "space-between",
											alignItems: "start",
											mb: 2,
										}}
									>
										<Box>
											<Box
												sx={{
													display: "flex",
													alignItems: "center",
													gap: 1,
													mb: 1,
												}}
											>
												{
													TIER_DISPLAY[selectedPlan]
														.icon
												}
												<Typography
													variant="h6"
													sx={{
														fontWeight: 700,
														color: "#1A2B4A",
													}}
												>
													{
														TIER_DISPLAY[
															selectedPlan
														].name
													}{" "}
													Plan
												</Typography>
												{TIER_DISPLAY[selectedPlan]
													.featured && (
													<Chip
														label="Most Popular"
														size="small"
														sx={{
															backgroundColor:
																"#2BA6AC",
															color: "white",
															fontWeight: 600,
														}}
													/>
												)}
											</Box>
											<Typography
												variant="h4"
												sx={{
													fontWeight: 700,
													color: "#2BA6AC",
												}}
											>
												{selectedPlan === "starter"
													? "Free"
													: catalogLoading
														? "…"
														: formatTierPrice(
																planFor(
																	selectedPlan,
																),
															)}
												{selectedPlan !== "starter" && (
													<Typography
														component="span"
														sx={{
															fontSize: "16px",
															color: "#5A6E7F",
															fontWeight: 400,
														}}
													>
														{" "}
														/ year
													</Typography>
												)}
											</Typography>
										</Box>
										<CheckCircle
											sx={{
												color: "#2BA6AC",
												fontSize: 28,
											}}
										/>
									</Box>

									<Typography
										variant="body2"
										sx={{ color: "#5A6E7F", mb: 2 }}
									>
										{TIER_DISPLAY[selectedPlan].description}
									</Typography>

									<Box sx={{ mt: 2 }}>
										{TIER_DISPLAY[
											selectedPlan
										].included.map((feature, idx) => (
											<FeatureItem
												key={`incl-${selectedPlan}-${idx}`}
												included={true}
											>
												<CheckCircle
													sx={{
														fontSize: 18,
														mr: 1.5,
														color: "#2BA6AC",
													}}
												/>
												<Typography variant="body2">
													{feature}
												</Typography>
											</FeatureItem>
										))}
									</Box>

									{TIER_DISPLAY[selectedPlan].excluded
										.length > 0 && (
										<Box sx={{ mt: 2 }}>
											<Button
												size="small"
												onClick={() =>
													setShowExclusions(
														(prev) => !prev,
													)
												}
												endIcon={
													<ExpandMoreIcon
														sx={{
															transform:
																showExclusions
																	? "rotate(180deg)"
																	: "none",
															transition:
																"transform 0.2s",
														}}
													/>
												}
												sx={{
													color: "#5A6E7F",
													textTransform: "none",
													fontSize: "13px",
													padding: 0,
													"&:hover": {
														background: "none",
														color: "#2BA6AC",
													},
												}}
											>
												{showExclusions
													? "Hide what's not included"
													: "What's not included"}
											</Button>
											<Collapse in={showExclusions}>
												<Box sx={{ mt: 1 }}>
													{TIER_DISPLAY[
														selectedPlan
													].excluded.map(
														(feature, idx) => (
															<FeatureItem
																key={`excl-${selectedPlan}-${idx}`}
																included={false}
															>
																<Close
																	sx={{
																		fontSize: 18,
																		mr: 1.5,
																		color: "#dee2e6",
																	}}
																/>
																<Typography variant="body2">
																	{feature}
																</Typography>
															</FeatureItem>
														),
													)}
												</Box>
											</Collapse>
										</Box>
									)}
								</PricingCard>

								<Typography
									variant="caption"
									sx={{
										color: "#5A6E7F",
										mt: 3,
										display: "block",
										textAlign: "center",
									}}
								>
									{selectedPlan === "starter"
										? "Cancel anytime"
										: "Secure payment processed by Stripe • Cancel anytime"}
								</Typography>
							</PricingSection>
						</Grid>
					</Grid>
				</SignupCard>
			</motion.div>
		</PageContainer>
	);
};

export default Signup;
